import { isAxiosError } from 'axios';

import { VISITOR_ID } from '~/constants/storages';
import { apiClient } from '~/services/api-service';
import { logger } from '~/utils/logger';

import { cookieService } from './cookie-service';

// List of campaign parameters to track
const PAID_CAMPAIGN_QUERY_PARAMETERS = [
  'gclid', // Google Click Identifier
  'fbclid', // Facebook Click Identifier
  'ttclid', // TikTok Click ID
  'msclkid', // Microsoft Click ID
  'li_fat_id', // LinkedIn Click ID
  'gbraid', // Google Braid (iOS related)
  'wbraid', // Google Web Braid (iOS related)
  'utm_id', // UTM Campaign ID
  'utm_source', // UTM Source
  'utm_medium', // UTM Medium
  'utm_campaign', // UTM Campaign Name
  'utm_term', // UTM Term
  'utm_content', // UTM Content
];

interface LandingData {
  timestamp: number;
  landingPageUrl: string;
  referrerUrl: string | null;
}

class VisitorAttributionService {
  /**
   * Check if the current URL contains any campaign parameters
   */
  private hasCampaignParameters(): boolean {
    const params = new URLSearchParams(window.location.search);
    return PAID_CAMPAIGN_QUERY_PARAMETERS.some((param) => params.has(param));
  }

  /**
   * Track landing page visit if conditions are met
   */
  async trackLanding(): Promise<void> {
    try {
      const isAuthenticated = cookieService.getCookie('access') || null;
      // Only track if user is not signed in and has campaign parameters
      if (isAuthenticated || !this.hasCampaignParameters()) {
        return;
      }

      const landingData: LandingData = {
        timestamp: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
        landingPageUrl: window.location.href,
        referrerUrl: document.referrer || '',
      };

      logger.log('Sending landing data:', landingData);

      const response = apiClient.post('/analytics/landing/', landingData, {
        withCredentials: true,
      });

      logger.log('Landing API response:', response);

      if (response.data.visitorId) {
        // Store visitor ID in localStorage as fallback
        localStorage.setItem(VISITOR_ID, response.data.visitorId);
        logger.log('✓ Visitor ID stored in localStorage:', response.data.visitorId);
      } else {
        logger.warn('No visitorId in response:', response.data);
      }
    } catch (error) {
      logger.error('Error tracking visitor attribution:', error);
      if (isAxiosError(error)) {
        logger.error('Response data:', error.response?.data);
        logger.error('Response status:', error.response?.status);
      }
      // Fail silently - don't disrupt user experience
    }
  }

  /**
   * Get visitor ID from localStorage (fallback)
   */
  private getVisitorID(): string | null {
    return localStorage.getItem(VISITOR_ID);
  }

  /**
   * Clear visitor ID after successful signup
   */
  clearVisitorID(): void {
    localStorage.removeItem(VISITOR_ID);
    logger.log('Visitor ID cleared');
  }

  /**
   * Get visitor data for signup request
   */
  getVisitorIDForSignup(): { visitorId?: string } {
    const visitorId = this.getVisitorID();
    return visitorId ? { visitorId } : {};
  }
}

// Export singleton instance
export const visitorAttributionService = new VisitorAttributionService();
